import { env } from '@/config/env';
import { MAP_DEFAULTS } from '@/config/map';
import { unwrapOpsObject } from '@/features/admin/utils/opsResponse';
import type {
  OpenMeteoPointCurrentData,
  OpenMeteoPointHourlyData,
} from '@/features/dashboard/types/openMeteo.types';
import type { WeatherSnapshot } from '@/features/realtime/types/realtime.types';
import { apiClient } from '@/lib/api-client';
function peelGatewayEnvelope(raw: unknown): unknown {
  let cur: unknown = raw;
  for (let depth = 0; depth < 8; depth++) {
    if (cur && typeof cur === 'object') {
      const o = cur as Record<string, unknown>;
      if ('data' in o && o.data !== undefined) {
        cur = o.data;
        continue;
      }
    }
    break;
  }
  return cur;
}

function defaultLatLon(): { lat: string; lon: string } {
  const lat = env.WEATHER_DEFAULT_LAT ?? MAP_DEFAULTS.CENTER.lat;
  const lon = env.WEATHER_DEFAULT_LON ?? MAP_DEFAULTS.CENTER.lng;
  return { lat: String(lat), lon: String(lon) };
}

function isOpenMeteoCurrent(x: unknown): x is OpenMeteoPointCurrentData {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return typeof o.latitude === 'number' && typeof o.longitude === 'number';
}

function isOpenMeteoHourly(x: unknown): x is OpenMeteoPointHourlyData {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return typeof o.latitude === 'number' && typeof o.longitude === 'number';
}

/** Lấy snapshot thời tiết qua Gateway Open-Meteo (cùng host với dashboard-summary). */
export async function fetchDashboardWeatherSnapshot(params: {
  year: number;
  hour: number;
}): Promise<WeatherSnapshot | null> {
  const { lat, lon } = defaultLatLon();
  const common = {
    lat,
    lon,
    timezone: 'auto',
    temperature_unit: 'celsius',
    wind_speed_unit: 'ms',
    precipitation_unit: 'mm',
  } as const;

  try {
    const [currentRes, hourlyRes] = await Promise.all([
      apiClient({
        url: '/api/weather/point-open-meteo',
        method: 'GET',
        params: { ...common, kind: 'current', forecast_days: '7' },
      }),
      apiClient({
        url: '/api/weather/point-open-meteo',
        method: 'GET',
        params: { ...common, kind: 'hourly', forecast_days: '3' },
      }),
    ]);

    const currentRaw = peelGatewayEnvelope(currentRes.data);
    const hourlyRaw = peelGatewayEnvelope(hourlyRes.data);

    const current = isOpenMeteoCurrent(currentRaw) ? currentRaw : null;
    const hourlyPayload = isOpenMeteoHourly(hourlyRaw) ? hourlyRaw : null;
    const hourly = hourlyPayload?.hourly;

    const c = current?.current;
    const tempC = typeof c?.temperature_2m === 'number' ? c.temperature_2m : 0;
    const windMs =
      typeof c?.wind_speed_10m === 'number'
        ? c.wind_speed_10m
        : typeof hourly?.wind_speed_10m?.[0] === 'number'
          ? hourly!.wind_speed_10m![0]!
          : 0;
    const windKmh = Math.round(windMs * 3.6 * 10) / 10;
    const humidityPct =
      typeof c?.relative_humidity_2m === 'number'
        ? Math.round(c.relative_humidity_2m)
        : typeof hourly?.relative_humidity_2m?.[0] === 'number'
          ? Math.round(hourly.relative_humidity_2m[0]!)
          : 0;

    let rainfall24hMm = 0;
    const prec = hourly?.precipitation ?? undefined;
    if (Array.isArray(prec)) {
      for (let i = 0; i < Math.min(24, prec.length); i++) {
        const v = prec[i];
        if (typeof v === 'number' && Number.isFinite(v)) rainfall24hMm += v;
      }
    } else if (typeof c?.precipitation === 'number') {
      rainfall24hMm = c.precipitation;
    }
    rainfall24hMm = Math.round(rainfall24hMm * 10) / 10;

    let trendTempC = 0;
    let trendRainMm = 0;
    let trendHumidityPct = 0;
    const tArr = hourly?.temperature_2m;
    const pArr = hourly?.precipitation;
    const hArr = hourly?.relative_humidity_2m;
    if (
      Array.isArray(tArr) &&
      tArr.length >= 2 &&
      typeof tArr[0] === 'number' &&
      typeof tArr[1] === 'number'
    ) {
      trendTempC = Math.round((tArr[0] - tArr[1]) * 10) / 10;
    }
    if (
      Array.isArray(pArr) &&
      pArr.length >= 2 &&
      typeof pArr[0] === 'number' &&
      typeof pArr[1] === 'number'
    ) {
      trendRainMm = Math.round((pArr[0] - pArr[1]) * 10) / 10;
    }
    if (
      Array.isArray(hArr) &&
      hArr.length >= 2 &&
      typeof hArr[0] === 'number' &&
      typeof hArr[1] === 'number'
    ) {
      trendHumidityPct = Math.round(hArr[0] - hArr[1]);
    }

    return {
      year: params.year,
      hour: params.hour,
      tempC: Math.round(tempC * 10) / 10,
      windKmh,
      rainfall24hMm,
      humidityPct,
      trendTempC,
      trendRainMm,
      trendHumidityPct,
    };
  } catch {
    return null;
  }
}

/**
 * Snapshot thời tiết theo kịch bản (bảng `dashboard_weather_snapshots` trên BE).
 * Khác với Open-Meteo — dùng khi RTK `getWeatherSnapshot` có `source: 'snapshot'`.
 */
export async function fetchDashboardWeatherCatalogSnapshot(params: {
  year: number;
  hour: number;
}): Promise<WeatherSnapshot | null> {
  try {
    const result = await apiClient({
      url: '/api/dashboard/weather-snapshot',
      method: 'GET',
      params: { year: params.year, hour: params.hour },
    });
    const raw = unwrapOpsObject<Record<string, unknown>>(result.data);
    if (!raw || typeof raw !== 'object') return null;
    const o = raw;
    const pick = (a: string, b: string) =>
      typeof o[a] === 'number' ? (o[a] as number) : typeof o[b] === 'number' ? (o[b] as number) : 0;
    return {
      year: typeof o.year === 'number' ? o.year : params.year,
      hour: typeof o.hour === 'number' ? o.hour : params.hour,
      tempC: pick('tempC', 'temp_c'),
      windKmh: pick('windKmh', 'wind_kmh'),
      rainfall24hMm: pick('rainfall24hMm', 'rainfall_24h_mm'),
      humidityPct: pick('humidityPct', 'humidity_pct'),
      trendTempC: pick('trendTempC', 'trend_temp_c'),
      trendRainMm: pick('trendRainMm', 'trend_rain_mm'),
      trendHumidityPct: pick('trendHumidityPct', 'trend_humidity_pct'),
    };
  } catch {
    return null;
  }
}
